import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ExpectedDelivery,
  ClarificationBadPartBins,
  ExpectedDeliveryClarification,
  ExpectedDeliveryCreateParams,
  ExpectedDeliveryReceipt,
  ReleaseWithReceiptsParams,
  ResolveClarificationParams
} from '../_models/expected-delivery.interface';

export interface ExpectedDeliveryFilterParams {
  status?: string;
  supplier_name?: string;
  created_by_id?: number;
  customer_id?: number;
  project_id?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ExpectedDeliveryService {
  private basePath = environment.apiURL + 'expected_deliveries';

  constructor(private http: HttpClient) {}

  /**
   * Alle erwarteten Lieferungen abrufen (mit optionalen Filtern)
   */
  public getAll(filters?: ExpectedDeliveryFilterParams): Observable<ExpectedDelivery[]> {
    let params = new HttpParams();
    if (filters) {
      if (filters.status) params = params.set('status', filters.status);
      if (filters.supplier_name) params = params.set('supplier_name', filters.supplier_name);
      if (filters.created_by_id) params = params.set('created_by_id', filters.created_by_id.toString());
      if (filters.customer_id) params = params.set('customer_id', filters.customer_id.toString());
      if (filters.project_id) params = params.set('project_id', filters.project_id.toString());
    }
    return this.http.get<ExpectedDelivery[]>(`${this.basePath}.json`, { params });
  }

  /**
   * Einzelne erwartete Lieferung abrufen
   */
  public getById(id: number | string): Observable<ExpectedDelivery> {
    return this.http.get<ExpectedDelivery>(`${this.basePath}/${id}.json`);
  }

  /**
   * Neue erwartete Lieferung erstellen
   */
  public create(data: ExpectedDeliveryCreateParams): Observable<{ success: boolean; id: number; expected_delivery: ExpectedDelivery }> {
    return this.http.post<{ success: boolean; id: number; expected_delivery: ExpectedDelivery }>(
      `${this.basePath}.json`,
      { expected_delivery: data }
    );
  }

  /**
   * Erwartete Lieferung aktualisieren
   */
  public update(id: number | string, data: Partial<ExpectedDeliveryCreateParams>): Observable<{ success: boolean; expected_delivery: ExpectedDelivery }> {
    return this.http.put<{ success: boolean; expected_delivery: ExpectedDelivery }>(
      `${this.basePath}/${id}.json`,
      { expected_delivery: data }
    );
  }

  /**
   * Erwartete Lieferung stornieren
   */
  public cancel(id: number | string): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.basePath}/${id}/cancel.json`, {});
  }

  /**
   * Erwartete Lieferung löschen (nur wenn keine Wareneingänge gebucht wurden)
   */
  public delete(id: number | string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.basePath}/${id}.json`);
  }

  /**
   * Wareneingänge-Historie abrufen
   */
  public getHistory(id: number | string): Observable<ExpectedDeliveryReceipt[]> {
    return this.http.get<ExpectedDeliveryReceipt[]>(`${this.basePath}/${id}/history.json`);
  }

  /**
   * Lieferung freigeben und Receipts erstellen (für pending_confirmation)
   * Erstellt Einbuchungs-Einträge, die vom Lager noch bestätigt werden müssen
   */
  public releaseWithReceipts(id: number | string, data: ReleaseWithReceiptsParams): Observable<{ success: boolean; expected_delivery: ExpectedDelivery; message?: string }> {
    return this.http.post<{ success: boolean; expected_delivery: ExpectedDelivery; message?: string }>(
      `${this.basePath}/${id}/release_with_receipts.json`,
      data
    );
  }

  /**
   * Lagerbestand pro Artikelvorlage in einem bestimmten Lager abfragen
   * Wird für die Anzeige bei internen Lieferungen verwendet
   */
  public getWarehouseStock(warehouseId: number): Observable<{
    warehouse: { id: number; label: string; address_company: string; address_city: string; address_country: string; address_firstname: string; address_lastname: string; address_state: string; address_street: string; address_street_no: string; address_zip: string };
    stock: Record<string, number>;
  }> {
    return this.http.get<any>(`${this.basePath}/warehouse_stock.json`, {
      params: { warehouse_id: warehouseId.toString() }
    });
  }

  /**
   * Gibt die URL zum Download eines spezifischen Lieferschein-PDF zurück
   */
  public getDeliveryNotePdfUrl(id: number | string, filename: string): string {
    return `${this.basePath}/${id}/delivery_note_pdfs/${filename}`;
  }

  // ==========================================================================
  // Klärfälle
  // ==========================================================================

  /**
   * Offene Positionen samt allem, was zur Entscheidung nötig ist: die Artikel,
   * die im Versand hängen, und bei Seriennummern die Auskunft, wo das Gerät
   * laut System steckt.
   */
  public getClarifications(id: number | string): Observable<{
    clarifications: ExpectedDeliveryClarification[];
    bad_part_bins: Record<number, ClarificationBadPartBins>;
  }> {
    return this.http.get<{
      clarifications: ExpectedDeliveryClarification[];
      bad_part_bins: Record<number, ClarificationBadPartBins>;
    }>(`${this.basePath}/${id}/clarifications.json`);
  }

  /** Entscheidet einen Klärfall ganz oder teilweise. */
  public resolveClarification(
    id: number | string,
    clarificationId: number,
    data: ResolveClarificationParams
  ): Observable<{ success: boolean; clarification: ExpectedDeliveryClarification; delivery: ExpectedDelivery }> {
    return this.http.post<{ success: boolean; clarification: ExpectedDeliveryClarification; delivery: ExpectedDelivery }>(
      `${this.basePath}/${id}/clarifications/${clarificationId}/resolve.json`,
      data
    );
  }

  /** Korrigiert die erwartete Menge einer Position (Schritt 1 der Klärung). */
  public updateItemQuantity(
    id: number | string,
    itemId: number,
    quantityOrdered: number
  ): Observable<{ success: boolean; delivery: ExpectedDelivery }> {
    return this.http.patch<{ success: boolean; delivery: ExpectedDelivery }>(
      `${this.basePath}/${id}/items/${itemId}/quantity.json`,
      { quantity_ordered: quantityOrdered }
    );
  }

  /** Korrigiert die vom Lager gezählte Menge einer Buchung (Schritt 1). */
  public updateReceiptQuantity(
    id: number | string,
    receiptId: number,
    quantity: number
  ): Observable<{ success: boolean; delivery: ExpectedDelivery }> {
    return this.http.patch<{ success: boolean; delivery: ExpectedDelivery }>(
      `${this.basePath}/${id}/receipts/${receiptId}/quantity.json`,
      { quantity }
    );
  }
}
